import { useCallback } from "react"
import { useTranslation } from "react-i18next"

import {
  DateFilterDto,
  DateFilterOperator,
  SlidingPreset,
} from "@tfg/types/types/date-filter.dto"

import { formatDate } from "~/utils/date"

export const useFiltersOperatorsLabelsTranslations = () => {
  const { t } = useTranslation(["common"])

  return {
    [DateFilterOperator.BETWEEN]: t("table.filter.operator.between"),
    [DateFilterOperator.EQUALS]: t("table.filter.operator.equals"),
    [DateFilterOperator.BEFORE]: t("table.filter.operator.before"),
    [DateFilterOperator.AFTER]: t("table.filter.operator.after"),
    [DateFilterOperator.IS_EMPTY]: t("table.filter.operator.empty"),
    [DateFilterOperator.IS_NOT_EMPTY]: t("table.filter.operator.notEmpty"),
    [DateFilterOperator.NOT_EQUALS]: t("table.filter.operator.notEquals"),
    [DateFilterOperator.IS_BEFORE_OR_EQUALS]: t(
      "table.filter.operator.beforeOrEquals"
    ),
    [DateFilterOperator.IS_AFTER_OR_EQUALS]: t(
      "table.filter.operator.afterOrEquals"
    ),
    [DateFilterOperator.IS_NOT_BETWEEN]: t("table.filter.operator.notBetween"),
  }
}

export type FiltersOperatorsValuesResolver = (filter: DateFilterDto) => string

export const useFiltersOperatorsValuesResolver = (
  slidingPresetLabelTranslationMapping: Record<SlidingPreset, string>
): FiltersOperatorsValuesResolver => {
  const { t } = useTranslation(["common"])

  const labelResolver = useCallback(
    (filter: DateFilterDto): string => {
      const { range, operator, preset } = filter

      const isDateRangeOperator =
        operator !== DateFilterOperator.IS_EMPTY &&
        operator !== DateFilterOperator.IS_NOT_EMPTY

      if (preset !== SlidingPreset.CUSTOM && isDateRangeOperator) {
        return slidingPresetLabelTranslationMapping[preset] ?? ""
      }

      const formattedStartDate = range?.startDate
        ? formatDate(range?.startDate)
        : ""
      const formattedEndDate = range?.endDate ? formatDate(range?.endDate) : ""

      const formattedDates =
        formattedStartDate && formattedEndDate
          ? `${formattedStartDate} - ${formattedEndDate}`
          : formattedStartDate || formattedEndDate

      const labelResolver = {
        [DateFilterOperator.BETWEEN]: `${t("table.filter.operator.between")} ${formattedDates}`,
        [DateFilterOperator.EQUALS]: `${t("table.filter.operator.equals")} ${formattedStartDate}`,
        [DateFilterOperator.BEFORE]: `${t("table.filter.operator.before")} ${formattedStartDate}`,
        [DateFilterOperator.AFTER]: `${t("table.filter.operator.after")} ${formattedStartDate}`,
        [DateFilterOperator.NOT_EQUALS]: `${t("table.filter.operator.notEquals")} ${formattedStartDate}`,
        [DateFilterOperator.IS_BEFORE_OR_EQUALS]: `${t("table.filter.operator.beforeOrEquals")} ${formattedStartDate}`,
        [DateFilterOperator.IS_AFTER_OR_EQUALS]: `${t("table.filter.operator.afterOrEquals")} ${formattedStartDate}`,
        [DateFilterOperator.IS_NOT_BETWEEN]: `${t("table.filter.operator.notBetween")} ${formattedDates}`,
        [DateFilterOperator.IS_NOT_EMPTY]: t("table.filter.operator.notEmpty"),
        [DateFilterOperator.IS_EMPTY]: t("table.filter.operator.empty"),
      }

      return labelResolver[operator]
    },
    [t, slidingPresetLabelTranslationMapping]
  )

  return labelResolver
}

export const useFiltersSlidingPresetsLabelsTranslations = () => {
  const { t } = useTranslation(["common"])

  return {
    [SlidingPreset.TODAY]: t("table.filter.date.preset.today"),
    [SlidingPreset.YESTERDAY]: t("table.filter.date.preset.yesterday"),
    [SlidingPreset.TOMORROW]: t("table.filter.date.preset.tomorrow"),
    [SlidingPreset.ONE_WEEK_AGO]: t("table.filter.date.preset.oneWeekAgo"),
    [SlidingPreset.ONE_WEEK_FROM_NOW]: t(
      "table.filter.date.preset.oneWeekFromNow"
    ),
    [SlidingPreset.ONE_MONTH_AGO]: t("table.filter.date.preset.oneMonthAgo"),
    [SlidingPreset.ONE_MONTH_FROM_NOW]: t(
      "table.filter.date.preset.oneMonthFromNow"
    ),
    [SlidingPreset.ONE_YEAR_AGO]: t("table.filter.date.preset.oneYearAgo"),
    [SlidingPreset.ONE_YEAR_FROM_NOW]: t(
      "table.filter.date.preset.oneYearFromNow"
    ),
    [SlidingPreset.CUSTOM]: t("table.filter.date.preset.custom"),
  }
}
